import { Plus, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getPaymentImages } from '../api/paymentImages';
import type { PaymentOverviewLineItemDto } from '../types/jobCostOverview';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });

interface PaymentsPanelProps {
  jobId: number;
  payments: PaymentOverviewLineItemDto[];
  paymentTotal: number;
  onAddPayment: () => void;
  onEditPayment: (payment: PaymentOverviewLineItemDto) => void;
}

export function PaymentsPanel({ jobId, payments, paymentTotal, onAddPayment, onEditPayment }: PaymentsPanelProps) {
  const handleViewImage = async (e: React.MouseEvent, paymentId: number) => {
    e.stopPropagation();
    const images = await getPaymentImages(jobId, paymentId);
    if (images.length > 0) {
      window.open(images[0].filePath, '_blank');
    }
  };
  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-border bg-gray-50 rounded-t-lg">
        <h4 className="text-xs font-semibold">Payments</h4>
        <Button variant="ghost" size="sm" onClick={onAddPayment} className="h-6 text-xs px-2">
          <Plus className="h-3 w-3 mr-1" />
          Add
        </Button>
      </div>
      <div className="p-2">
        {payments.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-2">No payments</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground">
                <th className="w-4 pb-1"></th>
                <th className="text-left pb-1 font-medium">Date</th>
                <th className="text-left pb-1 font-medium">Type</th>
                <th className="text-right pb-1 font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr
                  key={payment.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => onEditPayment(payment)}
                >
                  <td className="py-0.5">
                    {payment.hasImages && (
                      <button
                        onClick={(e) => handleViewImage(e, payment.id)}
                        className="hover:text-secondary transition-colors"
                        title="View document (opens in new tab)"
                      >
                        <ImageIcon className="h-4 w-4 text-secondary" />
                      </button>
                    )}
                  </td>
                  <td className="py-0.5">{formatDate(payment.paymentDate)}</td>
                  <td className="py-0.5">
                    {payment.paymentTypeName}
                    {payment.isPending && (
                      <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0">
                        Pending
                      </Badge>
                    )}
                  </td>
                  <td className="py-0.5 text-right">{formatCurrency(payment.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex justify-between items-center pt-1.5 mt-1.5 border-t border-border text-xs font-semibold">
          <span>Total</span>
          <span>{formatCurrency(paymentTotal)}</span>
        </div>
      </div>
    </div>
  );
}
